import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ICareerSkillNode } from 'core/hooks/CareerSkillGraph';
import { ISkillProfession } from 'core/hooks/Growth/transform/transformSkillDetail';

import { ISkillGraphIndex } from '../../model/skillGraphModel';
import { IPassportCourse } from '../../model/skillPassportCourses';
import { Block, BlockTitle, Details, Fact, FactRow, Meta, SecondaryLink } from './passportStyles';
import { SkillPassportLinks } from './SkillPassportLinks';
import { SkillPassportWhereNeeded } from './SkillPassportWhereNeeded';

/** Словари ЛИТЕРАЛОВ: сканер паритета локалей видит только их. */
const PRIORITY_KEY: Record<ICareerSkillNode['priority'], string> = {
  core: 'careerCenter.map.priority.core',
  important: 'careerCenter.map.priority.important',
  optional: 'careerCenter.map.priority.optional',
};

const WHY_KEY: Record<ICareerSkillNode['priority'], string> = {
  core: 'careerCenter.map.why.core',
  important: 'careerCenter.map.why.important',
  optional: 'careerCenter.map.why.optional',
};

/**
 * Паспорт, пункт 6 (ТЗ 0072, 3.3): связи и пояснения — под `<details>`:
 * зачем навык профессии, основа и что он открывает, где ещё нужен,
 * альтернативные курсы.
 *
 * 🔴 Свёрнуто по умолчанию. Где ещё нужен навык, сколько вакансий и какие есть
 * альтернативные курсы — это справка, а не ответ на «что мне делать». Пока
 * всё это стояло раскрытым второй колонкой, паспорт был шире карты и
 * отвечал на пять вопросов сразу.
 *
 * Альтернативы приходят УЖЕ без повторов основного курса (см.
 * `passportCourses`): дедупликация — свойство модели, а не этой разметки.
 */
interface ISkillPassportMore {
  index: ISkillGraphIndex;
  node: ICareerSkillNode;
  onOpenSkill: (slug: string) => void;
  professions: ISkillProfession[];
  loading: boolean;
  canOpenVacancies: boolean;
  vacanciesLocked: boolean;
  alternatives: IPassportCourse[];
}

export const SkillPassportMore = memo(
  ({
    index,
    node,
    onOpenSkill,
    professions,
    loading,
    canOpenVacancies,
    vacanciesLocked,
    alternatives,
  }: ISkillPassportMore) => {
    const { t } = useTranslation();

    return (
      <Details>
        <summary>{t('careerCenter.map.details')}</summary>
        <Inner>
          <Block aria-labelledby="skm-passport-why">
            <BlockTitle id="skm-passport-why">
              {t('careerCenter.map.why.title', { title: index.trackTitle })}
            </BlockTitle>
            <FactRow>
              <Fact $accent={node.priority === 'core'}>{t(PRIORITY_KEY[node.priority])}</Fact>
            </FactRow>
            <Meta>{t(WHY_KEY[node.priority])}</Meta>
          </Block>
          <SkillPassportLinks index={index} node={node} onOpenSkill={onOpenSkill} />
          <SkillPassportWhereNeeded
            node={node}
            professions={professions}
            loading={loading}
            canOpenVacancies={canOpenVacancies}
            vacanciesLocked={vacanciesLocked}
          />
          {alternatives.length > 0 && (
            <Block aria-labelledby="skm-passport-alternatives">
              <BlockTitle id="skm-passport-alternatives">
                {t('careerCenter.map.alternatives')}
              </BlockTitle>
              <List>
                {alternatives.map((row) => (
                  <li key={row.courseId}>
                    <Meta>
                      <SecondaryLink href={row.courseUrl} target="_blank" rel="noreferrer">
                        {row.courseName}
                      </SecondaryLink>
                      {row.completed && ` · ${t('careerCenter.map.providerDone')}`}
                    </Meta>
                  </li>
                ))}
              </List>
            </Block>
          )}
        </Inner>
      </Details>
    );
  },
);

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 8px;
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
`;
